import router from '@/router'
import camundaAPI from '@/services/camunda'
import componentAPI from '@/services/component'
import variablesProcessing from '@/utils/variablesProcessing'
import { getLocale, mergeProcessLocale, tf } from '@/i18n'
import lodash from 'lodash'

const state = {
  processDeployDialog: false,
  allDefinitions: null,
  startableDefinitions: null,
  definition: null,
  view: null,
  style: null,
  processInfoDialog: false,
  variables: null,
  instance: null,
  submitMode: false,
  tasks: null,
  processes: null,
  total: null,
  sort: {
    sorting: [
      { sortBy: 'startTime', sortOrder: 'desc' },
    ],
  },
  page: {
    current: 1,
    per: 10,
  },
}

const getters = {
  /* eslint-disable no-shadow */
  processDeployDialog: state => state.processDeployDialog,
  allDefinitions: state => state.allDefinitions,
  startableDefinitions: state => state.startableDefinitions,
  definition: state => state.definition,
  view: state => state.view,
  style: state => state.style,
  processInfoDialog: state => state.processInfoDialog,
  variables: state => state.variables,
  instance: state => state.instance,
  submitMode: state => state.submitMode,
  tasks: state => state.tasks,
  processes: state => state.processes,
  total: state => state.total,
  sort: state => state.sort,
  page: state => state.page,
}

const mutations = {
  set(state, { type, value }) {
    state[type] = value
  },
}

const actions = {
  getDefinitions({ commit }) {
    commit('set', { type: 'definition', value: null })
    return camundaAPI.definition.list().then((response) => {
      commit('set', { type: 'allDefinitions', value: lodash.filter(response.data, function(definition){ return definition.key != 'NEWS-01' && definition.key != 'USER-01' && definition.key != 'USER-02' && definition.key != 'USER-03' && definition.key != 'NOTIFY-03' }) })
      commit('set', { type: 'startableDefinitions', value: response.data.filter(definition => definition.startable) })
    })
  },

  selectDefinition({ dispatch, commit }, payload) {
    commit('set', { type: 'view', value: null })
    commit('set', { type: 'style', value: null })
    commit('set', { type: 'definition', value: null })
    return camundaAPI.definition.get(payload).then((response) => {
      commit('set', { type: 'definition', value: response.data })
      return dispatch('getView', 'start')
    })
  },

  selectInstance({ dispatch, commit }, payload) {
    commit('set', { type: 'instance', value: null })
    commit('set', { type: 'definition', value: null })
    commit('set', { type: 'variables', value: null })
    commit('set', { type: 'tasks', value: null })
    commit('set', { type: 'view', value: null })
    commit('set', { type: 'style', value: null })
    return camundaAPI.process.get(payload).then((response) => {
      commit('set', { type: 'instance', value: response.data })
      return camundaAPI.definition.get(state.instance.processDefinitionId).then((response) => {
        commit('set', { type: 'definition', value: response.data })
        return camundaAPI.process.tasks(payload).then((response) => {
          commit('set', { type: 'tasks', value: response.data })
          return dispatch('getView', 'process-info').then(() => dispatch('getVariables', state.instance.id))
        })
      })
    })
  },

  /* eslint-disable consistent-return */
  getView({ commit, state }, payload) {
    const processKey = state.definition.key
    if (process.env.NODE_ENV === 'development') {
      import(`${process.env.PROCESS_PATH}/${getLocale()}.json`).then((obj) => {
        mergeProcessLocale(processKey, obj)
      })
      import(`${process.env.PROCESS_PATH}/${payload}.vue`).then((obj) => {
        commit('set', { type: 'view', value: obj.default })
      })
    } else {
      return componentAPI.load(processKey, payload).then((form) => {
        if (form.locale && form.locale.length > 0) {
          mergeProcessLocale(processKey, JSON.parse(form.locale))
        }
        commit('set', { type: 'view', value: form.obj })
        commit('set', { type: 'style', value: form.style })
      })
    }
  },

  restore({ commit, state }) {
    return new Promise((resolve) => {
      const savedVariables = localStorage.getItem(`process-start-vars-${state.definition.key}`)
      commit('set', { type: 'variables', value: variablesProcessing.parse(savedVariables, true) })
      resolve()
    })
  },

  save({ state }, payload) {
    localStorage.setItem(`process-start-vars-${state.definition.key}`, variablesProcessing.stringify(payload, true))
  },

  submit({ commit, dispatch }, payload) {
    commit('set', { type: 'submitMode', value: true })
    camundaAPI.process.start(payload.definitionKey, { variables: variablesProcessing.stringify(payload.variables, false) }).then(() => {
      router.push('/process-search')
      commit('set', { type: 'submitMode', value: false })
      dispatch('tools/setSnackbar', tf('process.start.started'), { root: true })
      dispatch('task/getFilters', null, { root: true })
    }).catch((error) => {
      const errorMessage = error.response && error.response.data && error.response.data.message ? error.response.data.message : error
      console.log(`${tf('process.start.error')}: ${errorMessage}`)
      dispatch('tools/setSnackbar', `${tf('process.start.error')}: ${errorMessage}`, { root: true })
    })
  },

  // Запуск ещё одного инстанса без редиректа — для форм, которые шлют данные частями
  startInstance(context, payload) {
    return camundaAPI.process.start(payload.definitionKey, {
      variables: variablesProcessing.stringify(payload.variables, false),
    })
  },

  toggleProcessDeployDialog({ commit }, payload) {
    commit('set', { type: 'processDeployDialog', value: payload })
  },

  toggleProcessInfoDialog({ commit }, payload) {
    commit('set', { type: 'processInfoDialog', value: payload })
  },

  deploy({ commit, dispatch }, payload) {
    camundaAPI.definition.deploy(payload.processName, payload.deploymentSource, payload.file).then(() => {
      dispatch('getDefinitions')
      commit('set', { type: 'processDeployDialog', value: false })
      dispatch('tools/setSnackbar', `${tf('process.deploy.deployed')}: ${payload.file.name}`, { root: true })
    }).catch((error) => {
      const errorMessage = error.response && error.response.data && error.response.data.message ? error.response.data.message : error
      console.log(`${tf('process.deploy.error')}: ${errorMessage}`)
      dispatch('tools/setSnackbar', `${tf('process.deploy.error')}: ${errorMessage}`, { root: true })
    })
  },

  /* eslint-disable no-empty-pattern */
  diagramXml({ }, payload) {
    return camundaAPI.process.diagramXml(payload)
  },

  getVariables({ commit }, payload) {
    return camundaAPI.process.variables(payload).then((response) => {
      commit('set', { type: 'variables', value: variablesProcessing.parseHistory(response.data) })
    })
  },

  search({ commit }, payload) {
    if (payload.reset) {
      const page = { current: 1, per: state.page.per }
      commit('set', { type: 'page', value: page })
      commit('set', { type: 'total', value: null })
      commit('set', { type: 'processes', value: null })
    }

    if(payload.searchCriteria.taskPayload){
      camundaAPI.task.search(payload.searchCriteria.taskPayload).then((response) => {
        let taskProcessInstanceIds = lodash.values(lodash.mapValues(response.data, function(o) { return o.processInstanceId}))

        if (taskProcessInstanceIds.length > 0) {
          payload.searchCriteria = { ...payload.searchCriteria, processInstanceIds: taskProcessInstanceIds }
        }

        camundaAPI.process.count(payload.searchCriteria).then((response) => {
          commit('set', { type: 'total', value: response.data.count })
          camundaAPI.process.search(state.page, { ...payload.searchCriteria, ...state.sort }).then((response) => {
            if(payload.searchCriteria.searchVariables && response.data.length > 0){
              let processInstanceIds = lodash.values(lodash.mapValues(response.data, function(o) { return o.id}))
              let searchVariablesTotal = payload.searchCriteria.searchVariables.length;
              let searchVariablesCount = 0;
              lodash.forEach(payload.searchCriteria.searchVariables, function(searchVariable){
                camundaAPI.process.searchVariables(processInstanceIds, searchVariable).then((variablesResponse) => {
                  lodash.forEach(response.data, function(data){
                    let variableValue = lodash.filter(variablesResponse.data, function(variable){ return data.id == variable.processInstanceId });
                    if(variableValue && variableValue.length > 0 && variableValue[0].value){
                      data[searchVariable] = variableValue[0].value;
                    }
                  });
                  searchVariablesCount = searchVariablesCount + 1;
                  if(searchVariablesTotal === searchVariablesCount){
                    commit('set', { type: 'processes', value: response.data })
                  }
                });                  
              })
            } else {
              commit('set', { type: 'processes', value: response.data })
            }
          })
        })
      });
    } else {
      camundaAPI.process.count(payload.searchCriteria).then((response) => {
        commit('set', { type: 'total', value: response.data.count })
        camundaAPI.process.search(state.page, { ...payload.searchCriteria, ...state.sort }).then((response) => {
          if(payload.searchCriteria.searchVariables && response.data.length > 0){
            let processInstanceIds = lodash.values(lodash.mapValues(response.data, function(o) { return o.id}))
            let searchVariablesTotal = payload.searchCriteria.searchVariables.length;
            let searchVariablesCount = 0;
            lodash.forEach(payload.searchCriteria.searchVariables, function(searchVariable){
              camundaAPI.process.searchVariables(processInstanceIds, searchVariable).then((variablesResponse) => {
                lodash.forEach(response.data, function(data){
                  let variableValue = lodash.filter(variablesResponse.data, function(variable){ return data.id == variable.processInstanceId });
                  if(variableValue && variableValue.length > 0 && variableValue[0].value){
                    data[searchVariable] = variableValue[0].value;
                  }
                });
                searchVariablesCount = searchVariablesCount + 1;
                if(searchVariablesTotal === searchVariablesCount){
                  commit('set', { type: 'processes', value: response.data })
                }
              });
            })
          } else {
            commit('set', { type: 'processes', value: response.data })
          }
        })
      })
    }
  },

  paginateProcesses({ commit, state, dispatch }, payload) {
    const page = { current: payload.newPageNumber, per: state.page.per }
    commit('set', { type: 'page', value: page })
    dispatch('search', payload)
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}



// WEBPACK FOOTER //
// ./src/store/modules/process.js